import { Button, Card, Descriptions, Message, Popconfirm, Space, Table, Tag, Tooltip } from "@arco-design/web-react"
import { Link, useNavigate, useParams } from "react-router-dom";
import { WoFulfillmentAPI } from "@/apis/manufacturing";
import { Number, Currency } from "@/components/Numeric";
import { WOFulfillmentStatusDef, WOFulfillmentStatus } from "@/enums/manufacturing";
import { GLImpactType } from "@/enums/common";
import { EnumLabel } from "@/components/Enum";
import GLImpactButton from "@/components/GLImpactButton";
import { useCallback, useEffect, useState } from "react";

const statusColorMap = {
    [WOFulfillmentStatus.Fulfilled]: "green",
    [WOFulfillmentStatus.Voided]: "red",
};

export const WoFulfillmentInfo = () => {
    const navigate = useNavigate();
    const params = useParams();
    const [info, setInfo] = useState({});
    const [inventoryInUse, setInventoryInUse] = useState(false);

    const getInfo = useCallback(async () => {
        const fulfillmentInfo = await WoFulfillmentAPI.get(params.id);
        setInfo(fulfillmentInfo);
        const inUse = await WoFulfillmentAPI.checkInventoryInUse(params.id);
        setInventoryInUse(inUse);
    }, [params])

    const voidFulfillment = async () => {
        try {
            await WoFulfillmentAPI.voidFulfillment(params.id);
            Message.success("Fulfillment voided successfully");
            getInfo();
        } catch (error) {
            if (error.status === "910002") {
                Message.error("Cannot void fulfillment because its produced inventory has been picked, fulfilled, consumed, moved, or adjusted");
            } else {
                Message.error(error.message);
            }
        }
    }

    useEffect(() => {
        getInfo();
    }, [getInfo])

    return (
        <Card title={
            <Space>
                <span>{info.fulfillmentNo}</span>
                <Tag color={statusColorMap[info.status]}>
                    <EnumLabel enumObj={WOFulfillmentStatusDef} value={info.status} />
                </Tag>
            </Space>
        } extra={
            <Space>
                {info.status === WOFulfillmentStatus.Fulfilled && (
                    <Tooltip content="Cannot modify because inventory has been picked, fulfilled, consumed, moved, or adjusted" disabled={!inventoryInUse}>
                        <span>
                            <Button type="primary" disabled={inventoryInUse} onClick={() => navigate(`/workspace/manufacturing/wo-fulfillment/${params.id}/edit`)}>Edit</Button>
                        </span>
                    </Tooltip>
                )}
                {info.status === WOFulfillmentStatus.Fulfilled && (
                    <Tooltip content="Cannot void because inventory has been picked, fulfilled, consumed, moved, or adjusted" disabled={!inventoryInUse}>
                        <span>
                            <Popconfirm title="Are you sure you want to void this fulfillment?" onOk={() => voidFulfillment()}>
                                <Button type="primary" status="danger" disabled={inventoryInUse}>Void</Button>
                            </Popconfirm>
                        </span>
                    </Tooltip>
                )}
                <GLImpactButton type={GLImpactType.WoFulfillment} from={params.id} />
                <Button onClick={() => navigate("/workspace/manufacturing/wo-fulfillment")}>Back</Button>
            </Space>
        }>
            <Descriptions colon=":" column={3} data={[
                { label: "Fulfillment Date", value: info.fulfillDate || "" },
                {
                    label: "Pick Order#",
                    value: info.pickOrders?.map(po =>
                        <Link key={po.woPickOrderId} to={`/workspace/manufacturing/wo-pick-order/${po.woPickOrderId}`}>{po.pickOrderNo}</Link>
                    ).reduce((acc, el, i) => i === 0 ? [el] : [...acc, ", ", el], [])
                },
                { label: "WO#", value: <Link to={`/workspace/manufacturing/work-order/${info.workOrderId}`}>{info.workOrderNo}</Link> },
                { label: "BOM Name", value: info.bomId ? <Link to={`/workspace/manufacturing/bom/${info.bomId}`}>{info.bomName}</Link> : info.bomName },
                { label: "BOM Description", value: info.bomDescription },
                { label: "Fulfilled Qty", value: <Number value={info.fulfilledQty} /> },
            ]} />

            <div style={{ fontWeight: "bold", margin: "24px 0 8px" }}>Finished Items</div>
            <Table rowKey="id" columns={[
                { title: "Item Name", dataIndex: "itemName" },
                { title: "Description", dataIndex: "description" },
                { title: "BOM Qty", render: (col, record) => <Number value={record.bomQty} /> },
                { title: "Finished Qty", render: (col, record) => <Number value={record.quantity} /> },
                { title: "UOM", dataIndex: "uom" },
                { title: "Cost", render: (col, record) => <Currency value={record.cost} /> },
                { title: "Value", render: (col, record) => <Currency value={record.valueAmount} /> },
                {
                    title: "Warehouse Location",
                    render: (col, record) => {
                        const parts = [record.warehouseName, record.locationName].filter(Boolean);
                        return parts.join(" / ") || "-";
                    }
                },
                { title: "Lot#", dataIndex: "lot" },
                { title: "Expiration", dataIndex: "expiredDate" },
            ]}
                data={info.finishedItems}
                summary={(currentData) => {
                    const totalValue = currentData?.reduce((sum, row) => sum + (row.valueAmount || 0), 0) || 0;
                    return (
                        <Table.Summary>
                            <Table.Summary.Row>
                                <Table.Summary.Cell>Total</Table.Summary.Cell>
                                <Table.Summary.Cell />
                                <Table.Summary.Cell />
                                <Table.Summary.Cell />
                                <Table.Summary.Cell />
                                <Table.Summary.Cell />
                                <Table.Summary.Cell><Currency value={totalValue} /></Table.Summary.Cell>
                                <Table.Summary.Cell />
                                <Table.Summary.Cell />
                                <Table.Summary.Cell />
                            </Table.Summary.Row>
                        </Table.Summary>
                    );
                }}
            />

            <div style={{ fontWeight: "bold", margin: "24px 0 8px" }}>Raw Items</div>
            <Table rowKey="id" columns={[
                { title: "Item Name", dataIndex: "itemName" },
                { title: "Description", dataIndex: "description" },
                { title: "Pick Qty", render: (col, record) => <Number value={record.pickQty} /> },
                { title: "Used Qty", render: (col, record) => <Number value={record.usedQty} /> },
                { title: "UOM", dataIndex: "uom" },
                { title: "Cost", render: (col, record) => <Currency value={record.cost} /> },
                { title: "Value", render: (col, record) => <Currency value={record.valueAmount} /> },
                {
                    title: "Warehouse Location",
                    render: (col, record) => {
                        const parts = [record.warehouseName, record.locationName].filter(Boolean);
                        return parts.join(" / ") || "-";
                    }
                },
                { title: "Lot#", dataIndex: "lot" },
                { title: "Expiration", dataIndex: "expiredDate" },
            ]}
                data={info.rawItems}
            />
        </Card>
    )
}
